// Estado de cobro de la empresa + catálogo de licencias (#1008 / #1009).
//
// `GET /billing/status` dice si la empresa de la sesión ya pagó su licencia.
// Sólo las empresas de AUTOALTA pueden requerir plan; la cartera dada de alta a
// mano siempre responde `requiresPlan: false`.
//
// Regla: ante cualquier fallo, NO se bloquea a nadie. Dejar fuera a un cliente
// que sí pagó porque la API parpadeó es peor que dejar pasar un minuto a uno
// que no; el backend (derechos de #648) es quien niega de verdad.

import api from "../../utils/api";
import { updatePostHogCompanyGroup } from "../../posthog";
import type { SelectedPlan } from "../register/planService";

const apiUrl = import.meta.env.VITE_FIREBASE_API_URL;

export type BillingStatusValue =
  | "none"
  | "pending"
  | "active"
  | "past_due"
  | "unpaid"
  | "canceled";

export interface BillingStatus {
  companyId: string | null;
  selfServe: boolean;
  status: BillingStatusValue;
  requiresPlan: boolean;
  licenseId: string | null;
  licenseName: string | null;
  currentPeriodEnd: string | null;
  license: { id: string; name: string | null; apps: string[] } | null;
  /** ¿Este tenant puede cobrar ahora (Stripe configurado + un plan con precio)? Ausente en APIs viejas. */
  purchaseAvailable?: boolean | null;
  /** Caso de uso de la compañía (CC-CONSOLE #1613): filtra los planes. Ausente en APIs viejas. */
  useCase?: string | null;
  /**
   * ¿La compañía tiene un caso de uso GUARDADO (CC-CONSOLE #1634)? `useCase`
   * llega normalizado (sin dato = transportista); sólo con esto en `true` las
   * apps se ordenan por caso de uso. Ausente en APIs viejas.
   */
  useCaseStored?: boolean | null;
  /** Checkout de licencia iniciado hace poco y aún sin confirmar (evita pagar dos veces). */
  pendingCheckout?: { subscriptionId: string; createdAt: string; plan: string | null } | null;
}

/** Plan del catálogo público, con lo que la pantalla de planes necesita. */
export type CatalogPlan = SelectedPlan & {
  apps?: string[];
  highlighted?: boolean;
  order?: number;
};

const TTL_MS = 60 * 1000;
let cached: { token: string; at: number; value: BillingStatus | null } | null =
  null;

/** Olvida el estado en memoria (tras pagar, o al cambiar de sesión). */
export function invalidateBillingStatus() {
  cached = null;
}

/**
 * Lo que ya está en memoria para la sesión actual, sin pedir nada (el caso de
 * uso de `/apps` lo reutiliza en vez de volver a consultar). `null` si no se ha
 * cargado o es de otra sesión.
 */
export function peekBillingStatus(): BillingStatus | null {
  const token = localStorage.getItem("token") || "";
  if (!token || !cached || cached.token !== token) return null;
  return cached.value;
}

export async function fetchBillingStatus(
  { force = false } = {},
): Promise<BillingStatus | null> {
  const token = localStorage.getItem("token") || "";
  if (!token) return null;
  if (
    !force &&
    cached &&
    cached.token === token &&
    Date.now() - cached.at < TTL_MS
  ) {
    return cached.value;
  }
  try {
    const res = await api.get(`${apiUrl}/billing/status`);
    // `{ meta, data: { message, data: <payload> } }`, igual que /subscriptions.
    const body = (res.data?.data ?? res.data ?? {}) as Record<string, unknown>;
    const value = ((body.data ?? body) || null) as BillingStatus | null;
    cached = { token, at: Date.now(), value };
    // Grupo `company` de PostHog (CC-CONSOLE#1538): licencia y autoalta.
    if (value?.companyId) {
      updatePostHogCompanyGroup(value.companyId, {
        license: value.licenseName ?? null,
        license_id: value.licenseId ?? null,
        selfServe: value.selfServe === true,
        billing_status: value.status,
      });
    }
    return value;
  } catch {
    return null;
  }
}

/** Catálogo publicado + cupón del pago anual (null si está apagado). Nunca lanza. */
export async function fetchCatalogWithDiscount(): Promise<{
  plans: CatalogPlan[];
  annualDiscountPercent: number | null;
}> {
  try {
    const res = await api.get(`${apiUrl}/public/licenses`);
    const body = res.data;
    const data = body?.data ?? body ?? {};
    const list: CatalogPlan[] = data.licenses ?? [];
    const percent = Number(data.annual_discount?.percent);
    return {
      plans: [...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
      annualDiscountPercent:
        data.annual_discount && Number.isFinite(percent) && percent > 0 ? percent : null,
    };
  } catch {
    return { plans: [], annualDiscountPercent: null };
  }
}

/** Licencias publicadas, ordenadas como en el landing. Nunca lanza. */
export async function fetchCatalog(): Promise<CatalogPlan[]> {
  return (await fetchCatalogWithDiscount()).plans;
}
